import { z } from "zod";

import {
  CardProfileStatusValues,
  GlobalTagIndexSource,
  TagAssertionStatusValues,
  TagConfidenceBandValues,
  TagLayerValues,
  TagPrimaryScoringStatusValues,
  TagReviewStateValues,
  TagSourceValues,
  TagStatusValues,
  UnresolvedTagCandidateStatusValues,
  UnresolvedTagResolutionTypeValues,
  UserImportCardStatusStage,
  UserImportCardStatusValue,
  UserImportCardStatusValueValues,
  UserImportErrorCode,
  UserImportErrorPhase,
  UserImportSourceType,
  UserImportStatus,
} from "../types/enums.js";

export const TagKeySchema = z.object({
  layer: z.string(),
  slug: z.string(),
});

export const TagSchema = TagKeySchema.merge(
  z.object({
    name: z.string(),
    description: z.string().optional(),
    parentSlugs: z.array(z.string()).optional(),

    // Automatically added by DynamoDB Toolbox as _ct and _md and aliased for us
    modified: z.string().optional(),
    created: z.string().optional(),
  }),
);

const NonEmptyStringSchema = z.string().min(1);
const IsoTimestampSchema = z.string().datetime({ offset: true });
const ConfidenceSchema = z.number().gte(0).lte(1);
const SourceSummarySchema = z
  .array(z.enum(TagSourceValues))
  .superRefine((sources, ctx) => {
    const uniqueSources = new Set(sources);
    if (uniqueSources.size !== sources.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "sourceSummary must not contain duplicate sources.",
      });
    }
  });

export const TagLayerSchema = z.enum(TagLayerValues);
export const TagSourceSchema = z.enum(TagSourceValues);
export const TagStatusSchema = z.enum(TagStatusValues);
export const UnresolvedTagCandidateStatusSchema = z.enum(
  UnresolvedTagCandidateStatusValues,
);
export const UnresolvedTagResolutionTypeSchema = z.enum(
  UnresolvedTagResolutionTypeValues,
);
export const CardProfileStatusSchema = z.enum(CardProfileStatusValues);
export const TagAssertionStatusSchema = z.enum(TagAssertionStatusValues);
export const TagReviewStateSchema = z.enum(TagReviewStateValues);
export const TagConfidenceBandSchema = z.enum(TagConfidenceBandValues);
export const TagPrimaryScoringStatusSchema = z.enum(
  TagPrimaryScoringStatusValues,
);

export const CanonicalTagKeySchema = z
  .object({
    layer: TagLayerSchema,
    slug: NonEmptyStringSchema,
  })
  .strict();

export const CanonicalTagSchema = CanonicalTagKeySchema.extend({
  name: NonEmptyStringSchema,
  description: NonEmptyStringSchema,
  parentSlugs: z.array(NonEmptyStringSchema),
  status: TagStatusSchema,
  synonyms: z.array(NonEmptyStringSchema),
  exampleOracleIds: z.array(NonEmptyStringSchema),
  definitionVersion: NonEmptyStringSchema,
  reviewedAt: IsoTimestampSchema,
  reviewedBy: NonEmptyStringSchema,
}).strict();

export const UnresolvedTagCandidateKeySchema = z
  .object({
    source: TagSourceSchema,
    sourceNamespace: NonEmptyStringSchema,
    rawKey: NonEmptyStringSchema,
  })
  .strict();

export const UnresolvedTagCandidateSchema =
  UnresolvedTagCandidateKeySchema.extend({
    rawName: NonEmptyStringSchema,
    primaryLayer: TagLayerSchema,
    observedLayers: z.array(TagLayerSchema).min(1),
    affectedOracleCount: z.number().int().nonnegative(),
    sampleOracleIds: z.array(NonEmptyStringSchema).max(5),
    occurrenceCount: z.number().int().positive(),
    candidateStatus: UnresolvedTagCandidateStatusSchema,
    proposedCanonicalLayer: TagLayerSchema.optional(),
    proposedCanonicalSlug: NonEmptyStringSchema.optional(),
    resolutionType: UnresolvedTagResolutionTypeSchema.optional(),
    firstSeenAt: IsoTimestampSchema,
    lastSeenAt: IsoTimestampSchema,
    resolvedAt: IsoTimestampSchema.optional(),
    resolvedBy: NonEmptyStringSchema.optional(),
    notes: z.string(),
  }).strict();

export const CardTagAssertionKeySchema = z
  .object({
    oracleId: NonEmptyStringSchema,
    source: TagSourceSchema,
    sourceTagKey: NonEmptyStringSchema,
  })
  .strict();

export const CardTagAssertionSchema = CardTagAssertionKeySchema.extend({
  sourceTagName: NonEmptyStringSchema,
  assertionVersion: z.number().int().positive(),
  layer: TagLayerSchema,
  slug: NonEmptyStringSchema,
  assertionStatus: TagAssertionStatusSchema,
  confidence: ConfidenceSchema,
  confidenceBand: TagConfidenceBandSchema,
  evidence: z.record(z.unknown()),
  normalizationVersion: NonEmptyStringSchema,
  sourceVersion: NonEmptyStringSchema,
  isActive: z.boolean(),
  supersededAt: IsoTimestampSchema.optional(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
  reviewState: TagReviewStateSchema,
  reviewedBy: NonEmptyStringSchema.optional(),
  reviewedAt: IsoTimestampSchema.optional(),
}).strict();

export const NormalizedCardProfileTagSchema = z
  .object({
    layer: TagLayerSchema,
    slug: NonEmptyStringSchema,
    confidence: ConfidenceSchema,
    confidenceBand: TagConfidenceBandSchema,
    confidenceBreakdown: z.record(z.unknown()),
    primaryScoringStatus: TagPrimaryScoringStatusSchema,
    sourceSummary: SourceSummarySchema,
    evidenceSummary: z.record(z.unknown()),
    updatedAt: IsoTimestampSchema,
  })
  .strict();

export const NormalizedCardProfileKeySchema = z
  .object({
    oracleId: NonEmptyStringSchema,
  })
  .strict();

export const TagSourceVersionMapSchema = z
  .record(NonEmptyStringSchema, NonEmptyStringSchema)
  .superRefine((sourceVersions, ctx) => {
    const validSources = new Set<string>(TagSourceValues);
    Object.keys(sourceVersions).forEach((sourceKey) => {
      if (!validSources.has(sourceKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid sourceVersions key "${sourceKey}".`,
          path: [sourceKey],
        });
      }
    });
  });

export const NormalizedCardProfileSchema =
  NormalizedCardProfileKeySchema.extend({
    profileStatus: CardProfileStatusSchema,
    tags: z.array(NormalizedCardProfileTagSchema),
    normalizationVersion: NonEmptyStringSchema,
    sourceVersions: TagSourceVersionMapSchema,
    lastNormalizedAt: IsoTimestampSchema,
    reviewState: TagReviewStateSchema,
    reviewedBy: NonEmptyStringSchema.optional(),
    reviewedAt: IsoTimestampSchema.optional(),
  }).strict();

export const CardCacheKeySchema = z.object({
  scryfallId: z.string(),
  setCode: z.string(),
  collectorNumber: z.string(),
  oracleId: z.string(),
});

export const CardCacheSchema = CardCacheKeySchema.merge(
  z.object({
    raw: z.record(z.unknown()),
    fetchedAt: z.string(),
    ttl: z.number().optional(),

    modified: z.string().optional(),
    created: z.string().optional(),
  }),
);

export const TagCacheKeySchema = z.object({
  scryfallId: z.string(),
  setCode: z.string(),
  collectorNumber: z.string(),
  oracleId: z.string(),
});

export const TagCacheSchema = TagCacheKeySchema.merge(
  z.object({
    raw: z.record(z.unknown()),
    fetchedAt: z.string(),
    ttl: z.number().optional(),

    modified: z.string().optional(),
    created: z.string().optional(),
  }),
);

export const UserCardKeySchema = z.object({
  userId: z.string(),
  oracleId: z.string(),
  scryfallId: z.string(),
});

export const UserCardSchema = UserCardKeySchema.merge(
  z.object({
    name: z.string(),
    typeLine: z.string().optional(),
    cmc: z.number().optional(),
    colors: z.array(z.string()).optional(),
    colorIdentity: z.array(z.string()).optional(),
    setCode: z.string(),
    collectorNumber: z.string(),
    quantity: z.number(),
    importId: z.string(),
    oracleText: z.string().optional(),
    manaCost: z.string().optional(),
    power: z.string().optional(),
    toughness: z.string().optional(),
    loyalty: z.string().optional(),
    keywords: z.array(z.string()).optional(),
    lastSyncedAt: z.string(),
    cardCacheFetchedAt: z.string(),

    modified: z.string().optional(),
    created: z.string().optional(),
  }),
);

export const GlobalTagIndexKeySchema = z.object({
  layer: z.string(),
  slug: z.string(),
  source: z.nativeEnum(GlobalTagIndexSource),
  oracleId: z.string(),
  scryfallId: z.string(),
});

export const GlobalTagIndexSchema = GlobalTagIndexKeySchema.merge(
  z.object({
    ttl: z.number().optional(),

    modified: z.string().optional(),
    created: z.string().optional(),
  }),
);

export const UserTagIndexKeySchema = z
  .object({
    userId: z.string(),
    layer: TagLayerSchema,
    slug: NonEmptyStringSchema,
    oracleId: z.string(),
    scryfallId: z.string(),
  })
  .strict();

export const UserTagIndexSchema = UserTagIndexKeySchema.merge(
  z
    .object({
      importId: z.string(),
      confidence: ConfidenceSchema,
      confidenceBand: TagConfidenceBandSchema,
      primaryScoringStatus: TagPrimaryScoringStatusSchema,
      normalizationVersion: NonEmptyStringSchema,
      sourceSummary: SourceSummarySchema,

      modified: z.string().optional(),
      created: z.string().optional(),
    })
    .strict(),
);

export const UserImportCardStatusKeySchema = z.object({
  userId: z.string(),
  importId: z.string(),
  scryfallId: z.string(),
});

export const UserImportCardStatusStageSchema = z.enum([
  UserImportCardStatusStage.FETCH,
  UserImportCardStatusStage.WRITE,
]);
export const UserImportCardStatusValueSchema = z.enum(
  UserImportCardStatusValueValues,
);

export const UserImportCardStatusSchema = UserImportCardStatusKeySchema.merge(
  z.object({
    oracleId: z.string().optional(),
    name: z.string().optional(),
    typeLine: z.string().optional(),
    colors: z.array(z.string()).optional(),
    stage: UserImportCardStatusStageSchema,
    status: UserImportCardStatusValueSchema,
    errorMessage: z.string().optional(),
    updatedAt: z.string(),
    ttl: z.number().optional(),

    modified: z.string().optional(),
    created: z.string().optional(),
  }),
);

export const UserImportStateKeySchema = z.object({
  userId: z.string(),
  record: z.string(),
});

export const UserImportSourceTypeSchema = z.enum([
  UserImportSourceType.UPLOAD,
  UserImportSourceType.URL,
]);
export const UserImportStatusSchema = z.enum([
  UserImportStatus.AWAITING_UPLOAD,
  UserImportStatus.QUEUED,
  UserImportStatus.PROCESSING,
  UserImportStatus.IN_PROGRESS,
  UserImportStatus.COMMITTED,
  UserImportStatus.FAILED,
]);
export const UserImportErrorPhaseSchema = z.enum([
  UserImportErrorPhase.IMPORT,
  UserImportErrorPhase.QUEUE,
  UserImportErrorPhase.FETCH,
  UserImportErrorPhase.WRITE,
]);
export const UserImportErrorCodeSchema = z.enum([
  UserImportErrorCode.IMPORT_STALE_TIMEOUT,
  UserImportErrorCode.QUEUE_FAILED,
  UserImportErrorCode.SOURCE_READ_FAILED,
  UserImportErrorCode.FETCH_FAILED_SCRYFALL,
  UserImportErrorCode.FETCH_FAILED_TAGGER,
  UserImportErrorCode.FETCH_FAILED_MIXED,
  UserImportErrorCode.WRITE_FAILED_DYNAMO,
]);
const ImportStatusCountSchema = z.number().int().nonnegative().optional();
export const UserImportErrorSchema = z.object({
  code: UserImportErrorCodeSchema,
  phase: UserImportErrorPhaseSchema,
  retryable: z.boolean(),
  message: z.string(),
  failedCardCount: z.number().int().nonnegative().optional(),
  failedScryfallIds: z.array(z.string()).optional(),
});

export const UserImportCardStatusCountsByStatusSchema = z.object({
  [UserImportCardStatusValue.PENDING]: ImportStatusCountSchema,
  [UserImportCardStatusValue.FETCH_FAILED_SCRYFALL]: ImportStatusCountSchema,
  [UserImportCardStatusValue.FETCH_FAILED_TAGGER]: ImportStatusCountSchema,
  [UserImportCardStatusValue.READY_TO_WRITE]: ImportStatusCountSchema,
  [UserImportCardStatusValue.WRITE_FAILED]: ImportStatusCountSchema,
  [UserImportCardStatusValue.WRITTEN]: ImportStatusCountSchema,
});

export const UserImportCardStatusCountsByStageSchema = z.object({
  [UserImportCardStatusStage.FETCH]: ImportStatusCountSchema,
  [UserImportCardStatusStage.WRITE]: ImportStatusCountSchema,
});

export const UserImportStateSchema = UserImportStateKeySchema.merge(
  z.object({
    importId: z.string().optional(),
    currentImportId: z.string().optional(),
    pendingImportId: z.string().optional(),
    sourceType: UserImportSourceTypeSchema.optional(),
    fileName: z.string().optional(),
    fileSizeBytes: z.number().int().nonnegative().optional(),
    sourceUrl: z.string().url().optional(),
    storageBucket: z.string().optional(),
    storageKey: z.string().optional(),
    statusMessage: z.string().optional(),
    rowCount: z.number().int().nonnegative().optional(),
    uniqueCardCount: z.number().int().nonnegative().optional(),
    cardStatusTotalCount: z.number().int().nonnegative().optional(),
    cardStatusCountsByStatus:
      UserImportCardStatusCountsByStatusSchema.optional(),
    cardStatusCountsByStage: UserImportCardStatusCountsByStageSchema.optional(),
    queuedAt: z.string().optional(),
    uploadCompletedAt: z.string().optional(),
    processingStartedAt: z.string().optional(),
    status: UserImportStatusSchema,
    error: UserImportErrorSchema.optional(),
    warningCount: z.number().int().nonnegative().optional(),
    lastWarningAt: z.string().datetime({ offset: true }).optional(),
    startedAt: z.string().optional(),
    completedAt: z.string().optional(),
    updatedAt: z.string(),
    ttl: z.number().optional(),

    modified: z.string().optional(),
    created: z.string().optional(),
  }),
);
